import React from 'react';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import {
  AgnesAlert,
  AgnesAlertContent,
  AgnesButton,
  AgnesCheckbox,
  AgnesDataTable,
  AgnesDataTableColumn,
  AgnesIconButton,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
} from '@agnes/ui';
import {
  ChevronRight,
  Download,
  History,
  Layers,
  LineChart,
  ListIcon,
  Pencil,
  Tag,
} from 'lucide-react';
import { SearchInput } from '@/components/SearchInput';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { ExportButton } from '@/components/ExportButton';
import { useNavigate } from 'react-router-dom';
import {
  ORIGEM_SELECAO_MULTIPLA,
  ROTA_ACAO_EM_MASSA,
} from '@/pages/folha-pagamento/acao-em-massa/acaoEmMassa';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';
import { BadgeRubricaIncidencia } from '@/components/rubricas/incidencia/BadgeRubricaIncidencia';
import { AnaliseGrauExposicaoRisco } from '@/types/analise/AnaliseGrauExposicaoRisco';
import { notify } from '@/stores/useToast';
import ServiceRubricaTabela, {
  type RubricaContagem,
  type RubricaTabelaItem,
  type StatusAnaliseRubrica,
} from '@/services/ServiceRubricaTabela';
import FilterRubricaAnaliseLoteRegistros from '@/pages/folha-pagamento/index/tab-analise-lote/FilterRubricaAnaliseLoteRegistros';
import {
  type AppliedFilters,
  EMPTY_FILTERS,
  requestFilters,
} from '@/components/rubricas/rubricaLoteFilters';
import { BadgeEmAnalise } from '@/components/analise/BadgeEmAnalise';
import { BadgeRubricaSituacao } from '@/components/rubricas/situacao/BadgeRubricaSituacao';
import { RubricaSituacao } from '@/constants/rubricas/situacao/RubricaSituacao';
import { BadgeRubricaUtilizacaoFolhaPagamento } from '@/components/rubricas/utilizacao-folha-pagamento/BadgeRubricaUtilizacaoFolhaPagamento';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

const dataQa = 'rubricas-tabela';

type AgruparKey = 'nenhum' | 'ocorrencia' | 'descricao' | 'natureza' | 'incidencias';

const TAMANHO_BUSCA_AGRUPADA = 200;

const AGRUPAR: { key: AgruparKey; label: string; icon: React.ReactNode }[] = [
  { key: 'nenhum', label: 'Sem agrupamento', icon: <ListIcon /> },
  { key: 'ocorrencia', label: 'Ocorrência', icon: <LineChart className="h-4 w-4" /> },
  { key: 'descricao', label: 'Descrição da rubrica', icon: <Tag className="h-4 w-4" /> },
  { key: 'natureza', label: 'Natureza', icon: <Layers className="h-4 w-4" /> },
  { key: 'incidencias', label: 'Incidências', icon: <Layers className="h-4 w-4" /> },
];

const SEM_RECOMENDACAO = 'Em análise';
const semIncidenciaRecomendada = (r: RubricaTabelaItem) =>
  [r.cp, r.irrf, r.fgts, r.cprp, r.pis].every((codigo) => !codigo);

interface GrupoRubricas {
  chave: string;
  titulo: string;
  itens: RubricaTabelaItem[];
}

const tituloGrupo = (modo: AgruparKey, r: RubricaTabelaItem): string => {
  switch (modo) {
    case 'ocorrencia':
      return r.ocorrencia?.nome ?? 'Sem ocorrência';
    case 'descricao':
      return r.descricao ?? 'Sem descrição';
    case 'natureza':
      return r.naturezaCodigo != null
        ? `${r.naturezaCodigo} - ${r.naturezaNome ?? ''}`.trim()
        : SEM_RECOMENDACAO;
    case 'incidencias':
      return semIncidenciaRecomendada(r)
        ? SEM_RECOMENDACAO
        : [
            ['CP', r.cp],
            ['IRRF', r.irrf],
            ['FGTS', r.fgts],
            ['CPRP', r.cprp],
            ['PIS', r.pis],
          ]
            .map(([nome, codigo]) => `${nome}: ${codigo ?? '-'}`)
            .join(' · ');
    default:
      return '';
  }
};

const agruparRubricas = (modo: AgruparKey, itens: RubricaTabelaItem[]): GrupoRubricas[] => {
  const mapa = new Map<string, GrupoRubricas>();
  for (const r of itens) {
    const titulo = tituloGrupo(modo, r);
    const grupo = mapa.get(titulo) ?? { chave: titulo, titulo, itens: [] };
    grupo.itens.push(r);
    mapa.set(titulo, grupo);
  }
  return [...mapa.values()].sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));
};

const fmtCompetencia = (v: string | null) =>
  v && /^\d{4}-\d{2}$/.test(v) ? `${v.slice(5, 7)}/${v.slice(0, 4)}` : (v ?? '-');
const fmtDataHora = (v: string | null) => {
  if (!v) return '—';
  const d = new Date(v.replace(' ', 'T'));
  return Number.isNaN(d.getTime())
    ? v
    : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' });
};

export function TabTabelaRubricas() {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<'analise' | 'analisado'>('analise');
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [agrupar, setAgrupar] = React.useState<AgruparKey>('nenhum');
  const [recolhidos, setRecolhidos] = React.useState<Set<string>>(new Set());
  const [sel, setSel] = React.useState<Map<number, RubricaTabelaItem>>(new Map());
  const [filters, setFilters] = React.useState<AppliedFilters>(EMPTY_FILTERS);
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(50);
  const [result, setResult] = React.useState({
    content: [] as RubricaTabelaItem[],
    totalElements: 0,
    totalPages: 0,
  });
  const [counts, setCounts] = React.useState<RubricaContagem>({ emAnalise: 0, analisado: 0 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences('esocial-rubricas');
  const [exportando, setExportando] = React.useState(false);

  const exportar = async () => {
    setExportando(true);
    try {
      const statusAnalise: StatusAnaliseRubrica[] =
        tab === 'analise' ? ['EM_ANALISE', 'PENDENTE_DE_ESCLARECIMENTO'] : ['ANALISADO'];
      await ServiceRubricaTabela.exportar({ ...requestFilters(filters, search), statusAnalise });
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Não foi possível exportar as rubricas. Tente novamente.');
    } finally {
      setExportando(false);
    }
  };

  // Listagem da aba ativa.
  React.useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(false);

    const statusAnalise: StatusAnaliseRubrica[] =
      tab === 'analise' ? ['EM_ANALISE', 'PENDENTE_DE_ESCLARECIMENTO'] : ['ANALISADO'];

    const agrupado = agrupar !== 'nenhum';

    ServiceRubricaTabela.consultar({
      filters: { ...requestFilters(filters, search), statusAnalise },
      page: agrupado ? 0 : page,
      size: agrupado ? TAMANHO_BUSCA_AGRUPADA : size,
    })
      .then(({ data }) => {
        if (!ignore) {
          setResult(data);
          if (page > 0 && page >= data.totalPages) setPage(Math.max(0, data.totalPages - 1));
        }
      })
      .catch(() => {
        if (!ignore) setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [tab, search, page, size, filters, agrupar]);

  React.useEffect(() => {
    let ignore = false;
    ServiceRubricaTabela.contar(requestFilters(filters, search))
      .then(({ data }) => {
        if (!ignore) setCounts(data);
      })
      .catch(() => {
        if (!ignore) setCounts({ emAnalise: 0, analisado: 0 });
      });
    return () => {
      ignore = true;
    };
  }, [search, filters]);

  const toggle = (id: number) =>
    setSel((s) => {
      const n = new Map(s);
      const rubrica = result.content.find((item) => item.idAnaliseRubrica === id);
      if (n.has(id)) n.delete(id);
      else if (rubrica) n.set(id, rubrica);
      return n;
    });
  const handleAgrupar = (key: AgruparKey) => {
    setAgrupar(key);
    setPage(0);
    setRecolhidos(new Set());
  };
  const toggleGrupo = (chave: string) =>
    setRecolhidos((s) => {
      const n = new Set(s);
      if (n.has(chave)) n.delete(chave);
      else n.add(chave);
      return n;
    });
  const grupos = React.useMemo(
    () => (agrupar === 'nenhum' ? [] : agruparRubricas(agrupar, result.content)),
    [agrupar, result.content],
  );
  const allSel =
    result.content.length > 0 && result.content.every((r) => sel.has(r.idAnaliseRubrica));

  const columns: AgnesDataTableColumn<RubricaTabelaItem>[] = [
    {
      key: 'sel',
      header: '',
      width: 48,
      toggleable: false,
      headerRender: () => (
        <AgnesCheckbox
          aria-label="Selecionar todas as rubricas desta página"
          checked={allSel}
          onCheckedChange={() =>
            setSel((current) => {
              const next = new Map(current);
              result.content.forEach((rubrica) => {
                if (allSel) next.delete(rubrica.idAnaliseRubrica);
                else next.set(rubrica.idAnaliseRubrica, rubrica);
              });
              return next;
            })
          }
        />
      ),
      render: (r) => (
        <AgnesCheckbox
          aria-label={`Selecionar rubrica ${r.idAnaliseRubrica}`}
          checked={sel.has(r.idAnaliseRubrica)}
          onCheckedChange={() => toggle(r.idAnaliseRubrica)}
        />
      ),
    },
    {
      key: 'codigo',
      header: 'Código / Descrição',
      width: 220,
      render: (r) => (
        <div data-qa={`${dataQa}-celula-codigo-${r.idAnaliseRubrica}`} className="leading-tight">
          <p className="font-semibold text-agnes-text">{r.codigoRubrica ?? '-'}</p>
          <p className="text-sm text-agnes-text-muted">{r.descricao ?? '-'}</p>
        </div>
      ),
    },
    {
      key: 'naturezaCodigo',
      header: 'Natureza',
      width: 110,
      render: (r) => r.naturezaCodigo ?? SEM_RECOMENDACAO,
    },
    {
      key: 'naturezaNome',
      header: 'Nome da natureza',
      width: 180,
      render: (r) => r.naturezaNome ?? SEM_RECOMENDACAO,
    },
    { key: 'idTabela', header: 'Id. tabela', width: 120 },
    {
      key: 'inicioValidade',
      header: 'Início validade',
      width: 130,
      render: (r) => <span className="whitespace-nowrap">{fmtCompetencia(r.inicioValidade)}</span>,
    },
    {
      key: 'fimValidade',
      header: 'Fim validade',
      width: 120,
      render: (r) => <span className="whitespace-nowrap">{fmtCompetencia(r.fimValidade)}</span>,
    },
    {
      key: 'inc',
      header: 'Incidências',
      width: 340,
      render: (r) =>
        semIncidenciaRecomendada(r) ? (
          <span
            data-qa={`${dataQa}-celula-incidencias-${r.idAnaliseRubrica}`}
            className="text-agnes-text-muted"
          >
            {SEM_RECOMENDACAO}
          </span>
        ) : (
          <div
            data-qa={`${dataQa}-celula-incidencias-${r.idAnaliseRubrica}`}
            className="flex gap-1.5"
          >
            <BadgeRubricaIncidencia nome="CP" codigo={r.cp ?? undefined} />
            <BadgeRubricaIncidencia nome="IRRF" codigo={r.irrf ?? undefined} />
            <BadgeRubricaIncidencia nome="FGTS" codigo={r.fgts ?? undefined} />
            <BadgeRubricaIncidencia nome="CPRP" codigo={r.cprp ?? undefined} />
            <BadgeRubricaIncidencia nome="PIS" codigo={r.pis ?? undefined} />
          </div>
        ),
    },
    {
      key: 'grauRisco',
      header: 'Grau de risco',
      width: 150,
      render: (r) =>
        r.grauRisco ? (
          <BadgeGrauExposicaoRisco grau={r.grauRisco as AnaliseGrauExposicaoRisco} />
        ) : (
          <BadgeEmAnalise />
        ),
    },
    {
      key: 'ocorrencia',
      header: 'Ocorrência',
      width: 180,
      render: (r) => <span className="text-sm text-agnes-text">{r.ocorrencia?.nome ?? '—'}</span>,
    },
    {
      key: 'dataProcessamento',
      header: 'Data processamento',
      width: 170,
      render: (r) => <span className="whitespace-nowrap">{fmtDataHora(r.dataProcessamento)}</span>,
    },
    {
      key: 'situacao',
      header: 'Situação',
      width: 130,
      render: (r) =>
        r.situacao ? (
          <BadgeRubricaSituacao situacao={r.situacao as RubricaSituacao} />
        ) : (
          <span className="text-agnes-text-muted">—</span>
        ),
    },
    {
      key: 'folha',
      header: 'Folha',
      width: 130,
      render: (r) => (
        <BadgeRubricaUtilizacaoFolhaPagamento
          utilizacao={r.usoEmFolha ? 'UTILIZADA' : 'NAO_UTILIZADA'}
        />
      ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 110,
      align: 'center',
      toggleable: false,
      render: (item) => (
        <div className="flex items-center justify-center gap-1">
          <AgnesIconButton
            data-qa={`${dataQa}-acao-editar-${item.idAnaliseRubrica}`}
            aria-label="Editar"
            size="sm"
            onClick={() =>
              navigate(`/esocial/rubricas/analise/${item.idAnaliseRubrica}`, {
                state: { ids: result.content.map((r) => r.idAnaliseRubrica) },
              })
            }
          >
            <Pencil className="h-4 w-4" />
          </AgnesIconButton>

          <AgnesIconButton
            data-qa={`${dataQa}-acao-historico-${item.idAnaliseRubrica}`}
            aria-label="Histórico"
            title={item.possuiHistorico ? 'Histórico' : 'Sem histórico registrado'}
            disabled={!item.possuiHistorico}
            size="sm"
            onClick={() =>
              navigate(`/esocial/rubricas/analise/${item.idAnaliseRubrica}?tab=historico`, {
                state: { ids: result.content.map((r) => r.idAnaliseRubrica) },
              })
            }
          >
            <History className="h-4 w-4" />
          </AgnesIconButton>
        </div>
      ),
    },
  ];

  return (
    <div data-qa={dataQa} className="rounded-xl border border-agnes-border bg-agnes-bg-surface p-4">
      <AgnesTabs
        value={tab}
        onValueChange={(value) => {
          setTab(value as 'analise' | 'analisado');
          setPage(0);
        }}
      >
        <AgnesTabsList data-qa={`${dataQa}-abas`}>
          <AgnesTabsTrigger data-qa={`${dataQa}-aba-analise`} value="analise">
            Em análise
            <span className="ml-1 rounded-full bg-agnes-primary/10 px-2 text-xs text-agnes-primary">
              {counts.emAnalise.toLocaleString('pt-BR')}
            </span>
          </AgnesTabsTrigger>

          <AgnesTabsTrigger data-qa={`${dataQa}-aba-analisado`} value="analisado">
            Analisado
            <span className="ml-1 rounded-full bg-agnes-primary/10 px-2 text-xs text-agnes-primary">
              {counts.analisado.toLocaleString('pt-BR')}
            </span>
          </AgnesTabsTrigger>
        </AgnesTabsList>

        <AgnesTabsContent value={tab}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-[320px] flex-1">
              <SearchInput
                value={searchInput}
                onValueChange={setSearchInput}
                onSearch={(value) => {
                  setSearch(value.trim());
                  setPage(0);
                }}
                placeholder="Pesquisar por código, descrição, natureza, ID tabela ou ocorrência"
              />
            </div>

            <FilterRubricaAnaliseLoteRegistros
              dataQa="rubricas-tabela-filtro"
              titulo="Filtros da tabela de rubricas"
              filters={filters}
              onApply={(f) => {
                setFilters(f);
                setPage(0);
              }}
            />

            <ColumnsMenu
              data-qa="rubricas-colunas"
              columns={columns}
              hiddenColumnKeys={hiddenColumnKeys}
              onToggleColumn={toggleColumn}
            />

            <ExportButton
              dataQa={`${dataQa}-exportar`}
              variant="outlined"
              startIcon={<Download className="h-4 w-4" />}
              onClick={exportar}
              disabled={exportando}
            >
              Exportar
            </ExportButton>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-sm font-medium text-agnes-text-muted">Agrupar por:</span>
            {AGRUPAR.map((g) => (
              <button
                key={g.key}
                type="button"
                onClick={() => handleAgrupar(g.key)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${agrupar === g.key ? 'bg-agnes-primary text-white' : 'border border-agnes-border text-agnes-text hover:border-agnes-primary'}`}
              >
                {g.icon}
                {g.label}
              </button>
            ))}
            <span className="ml-auto text-sm text-agnes-text-muted">
              {result.totalElements.toLocaleString('pt-BR')} rubricas
            </span>
          </div>

          {error && (
            <AgnesAlert data-qa={`${dataQa}-erro`} preset="error" className="mt-4">
              <AgnesAlertContent>
                Não foi possível carregar as rubricas. Tente novamente.
              </AgnesAlertContent>
            </AgnesAlert>
          )}

          {agrupar === 'nenhum' ? (
            <TableWrapper data-qa={`${dataQa}-tabela`} className="mt-4">
              <AgnesDataTable<RubricaTabelaItem>
                columns={columns}
                hiddenColumnKeys={hiddenColumnKeys}
                data={result.content}
                rowKey={(r) => String(r.idAnaliseRubrica)}
                loading={loading}
                pagination={{
                  page,
                  pageSize: size,
                  total: result.totalElements,
                  serverSide: true,
                  onPageChange: setPage,
                  onPageSizeChange: (s) => {
                    setSize(s);
                    setPage(0);
                  },
                }}
              />
            </TableWrapper>
          ) : (
            <div data-qa={`${dataQa}-grupos`} className="mt-3 flex flex-col gap-3">
              {result.totalElements > result.content.length && (
                <AgnesAlert data-qa={`${dataQa}-aviso-agrupamento`} preset="warning">
                  <AgnesAlertContent>
                    Exibindo as primeiras {result.content.length.toLocaleString('pt-BR')} de{' '}
                    {result.totalElements.toLocaleString('pt-BR')} rubricas. Refine os filtros para
                    reduzir o resultado.
                  </AgnesAlertContent>
                </AgnesAlert>
              )}

              {!loading && grupos.length === 0 && (
                <p className="py-6 text-center text-sm text-agnes-text-muted">
                  Nenhuma rubrica encontrada.
                </p>
              )}

              {grupos.map((g) => {
                const aberto = !recolhidos.has(g.chave);
                return (
                  <section
                    key={g.chave}
                    data-qa={`${dataQa}-grupo`}
                    className="rounded-xl border border-agnes-border"
                  >
                    <button
                      type="button"
                      aria-expanded={aberto}
                      onClick={() => toggleGrupo(g.chave)}
                      className="flex w-full items-center gap-2 px-4 py-3 text-left"
                    >
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${aberto ? 'rotate-90' : ''}`}
                      />
                      <span className="font-semibold text-agnes-text">{g.titulo}</span>
                      <span className="rounded-full bg-agnes-primary/10 px-2 text-xs text-agnes-primary">
                        {g.itens.length.toLocaleString('pt-BR')}
                      </span>
                    </button>

                    {aberto && (
                      <div className="border-t border-agnes-border [&_.overflow-x-auto]:max-h-[60vh]">
                        <AgnesDataTable<RubricaTabelaItem>
                          columns={columns}
                          hiddenColumnKeys={hiddenColumnKeys}
                          data={g.itens}
                          rowKey={(r) => String(r.idAnaliseRubrica)}
                          loading={loading}
                        />
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </AgnesTabsContent>
      </AgnesTabs>

      {sel.size > 0 && (
        <div className="fixed bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-4 rounded-xl bg-agnes-primary px-4 py-3 text-white shadow-lg">
          <span className="rounded-full bg-agnes-success px-2 py-0.5 text-sm font-medium text-white">
            {sel.size} selecionadas
          </span>

          <span className="text-sm">Aplique uma ação em massa às rubricas selecionadas.</span>

          <AgnesButton variant="text" className="!text-white" onClick={() => setSel(new Map())}>
            Limpar seleção
          </AgnesButton>

          <AgnesButton
            variant="secondary"
            startIcon={<Layers className="h-4 w-4" />}
            onClick={() => {
              const rubricas = [...sel.values()];
              navigate(ROTA_ACAO_EM_MASSA, {
                state: {
                  rubricas,
                  origem:
                    rubricas.length === 1
                      ? (rubricas[0].descricao ?? ORIGEM_SELECAO_MULTIPLA)
                      : ORIGEM_SELECAO_MULTIPLA,
                },
              });
            }}
          >
            Ação em massa
          </AgnesButton>
        </div>
      )}

      {/* Filtro/agrupamento/exportação: estrutura mantida (features pós-core). */}
    </div>
  );
}
